import {
  Box,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useState } from "react";
import { labelSty } from "../UIElements/styles-input";

import useResponsive from "../../hooks/useResponsive";

import { InputCustomDisableSize } from "../UIElements/input-ui";
import { Dayjs } from "dayjs";
import { LoadingButton } from "@mui/lab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { PATH_PROFILE } from "../../routes/path";
import { useNavigate } from "react-router-dom";

import SelectInputMultiple from "../UIElements/select-multiple";
import { FacilityDataPropsType } from "../../interface/users";
import { specialitySelectType } from "../../enum/authentication";
import { apiEditFacilityInfo } from "../../api/users";

const parseSpecialties = (specialties?: string) =>
  (specialties ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const ProfileEditContent = ({
  facility_info,
}: {
  facility_info: FacilityDataPropsType;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>({
    start_date: null,
    end_date: null,
  });
  const [profile, setProfile] = useState<any>({
    contact_job_title: facility_info?.contact_job_title ?? "",
    contact_name: facility_info?.contact_name ?? "",
    phone_number: facility_info?.phone_number ?? "",
    address: facility_info?.address ?? "",
    city: facility_info?.city ?? "",
    state: facility_info?.state ?? "",
    facility_size: facility_info?.facility_size,
  });

  const [specialities, setSpecialities] = useState<string[]>(
    parseSpecialties(facility_info?.specialties),
  );

  const handleSpecialities = (
    event: SelectChangeEvent<typeof specialities>,
  ) => {
    const {
      target: { value },
    } = event;
    setSpecialities(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };
  // set up react query mutation
  const mutation = useMutation({
    mutationFn: (fieldValues: any) => apiEditFacilityInfo(fieldValues),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        queryClient.invalidateQueries({ queryKey: ["profileDetails"] });
        queryClient.invalidateQueries({ queryKey: ["profileInfo"] });
        navigate(PATH_PROFILE.root);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });

  const handleSubmit = () => {
    const specialties = specialities
      .map((item) => item.trim())
      .filter(Boolean)
      .join(", ");
    mutation.mutate({ ...profile, specialties });
  };

  return (
    <Box sx={{ p: 4, m: 3, background: "white", borderRadius: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 500 }}>
        Kindly note, not all informattion submitted can be changed.
      </Typography>
      <Box>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Contact Title</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile?.contact_job_title}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  contact_job_title: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Contact name </InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile.contact_name}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  contact_name: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Phone number</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile?.phone_number}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  phone_number: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Address</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile.address}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  address: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>City</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile?.city}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  city: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>State</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={false}
              value={profile.state}
              onchange={(e: any) =>
                setProfile((prev: any) => ({
                  ...prev,
                  state: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
          <InputLabel sx={{ ...labelSty, mb: 0 }}>Facility Size</InputLabel>
          <InputCustomDisableSize
            placeholder={""}
            disable={false}
            value={profile?.facility_size}
            onchange={(e: any) =>
              setProfile((prev: any) => ({
                ...prev,
                facility_size: e.target.value,
              }))
            }
            type={"text"}
            size={"medium"}
            styles={undefined}
          />
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%", mb: 4 }}>
            <SelectInputMultiple
              value={specialities}
              onChange={handleSpecialities}
              label={"Specialties"}
              disabled={false}
              options={specialitySelectType}
            />
          </Stack>
        </Stack>

        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          type="submit"
          loading={mutation.isPending}
          sx={{
            width: isMedium ? 180 : 250,
            fontWeight: 700,
            borderRadius: 1,
            margin: "1rem 0",
          }}
        >
          Submit
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ProfileEditContent;
